/* eslint-disable @typescript-eslint/no-explicit-any */
import { deepCopy } from '../../helpers';
import { SURROUND_MATERIAL_THICKNESS, type SurroundMaterialRoleThickness } from '../../blueprint-classes/surround-material/constants';

/**
 * Schema update from version 10 to version 11
 * - Fixes corrupted panel depths from a bug where stacked stone per-role
 *   thicknesses (front=1.5, back=1, sides=1) were applied to ALL materials
 * - Corrects panel depth to match the actual surround material thickness
 * - Recalculates side panel widths when depths are corrected
 */

const getThicknessForRole = (materialType: string, role: string): number => {
	const entry = SURROUND_MATERIAL_THICKNESS[materialType];
	if (entry === undefined) return 0;
	if (typeof entry === 'number') return entry;
	return (entry as SurroundMaterialRoleThickness)[role] ?? 0;
};

const fixPanelDepths = (island: any): void => {
	const { panels } = island;
	if (!panels?.children?.length) return;

	const materialType = panels.surroundMaterial?.type
		?? island.surround?.type
		?? '';
	if (!materialType) return;

	// Skip stacked stone — old SS kitchens have uniform 1.5" depths which is
	// their correct legacy state. Only non-stone materials were corrupted.
	if (materialType === 'stone') return;

	const thickness = getThicknessForRole(materialType, 'front');
	if (!thickness) return;

	// For non-stone uniform materials, all panels should have the same depth.
	// If any panel has a depth that doesn't match, it was corrupted by the bug.
	const { frontPanelId } = panels;
	const islandDepth = island.size?.depth;
	let depthCorrected = false;

	panels.children.forEach((p: any) => {
		const size = p.size ?? p.metadata?.size;
		if (!size) return;

		if (size.depth !== thickness) {
			size.depth = thickness;
			depthCorrected = true;
		}

		// Write size back to both locations for compatibility
		if (p.metadata) p.metadata.size = size;
		p.size = size;
	});

	// If depths were corrected, fix side panel widths
	if (depthCorrected && islandDepth) {
		const correctSideWidth = islandDepth - (thickness + thickness);
		panels.children.forEach((p: any) => {
			if (p.label === 'left' || p.label === 'right') {
				const size = p.size ?? p.metadata?.size;
				if (size) {
					size.width = correctSideWidth;
					if (p.metadata) p.metadata.size = size;
					p.size = size;
				}
			}
		});
	}
};

export const u11 = (blueprint: any): any => {
	console.log('schema-update:process-update:11:started');

	try {
		const updatedBlueprint = deepCopy(blueprint);
		updatedBlueprint.schemaVersion = 11;

		if (updatedBlueprint.kitchens?.children) {
			updatedBlueprint.kitchens.children.forEach((kitchen: any) => {
				const islands = kitchen.design?.islands?.children;
				if (!islands?.length) return;

				islands.forEach((island: any) => {
					// Only fix standard islands (4 panels), not corner islands (5 panels)
					if (island.panels?.children?.length === 4) {
						fixPanelDepths(island);
					}
				});
			});
		}

		console.log('schema-update:process-update:11:completed');
		return updatedBlueprint;
	} catch (err) {
		console.error('schema-update:process-update:11:error', err);
		return null;
	}
};
